import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BaseComponent } from '../base/base.component';
import { alphaNumericHyphenDotPattern } from '../../validators/alpha-numeric-hyphen-dot';
import {
  AutocompleteComponent,
  AutocompleteFilterEvent,
} from '../core/controls/autocomplete.component';

/** Letters plus hyphen/apostrophe (spaces handled separately for full-name split). */
const nameTokenPattern = /^[a-zA-Z'-]+$/;
const multiWordNamePattern = /^[a-zA-Z'-]+(\s+[a-zA-Z'-]+)+$/;
/** After spaces removed: alphanumeric plus hyphen or apostrophe (non-claim IDs). */
const searchIdCompactPattern = /^[a-zA-Z0-9\-']*$/;

@Component({
  selector: 'app-quick-search',
  imports: [
    ReactiveFormsModule,
    AutocompleteComponent,
    RouterModule,
    CommonModule,
  ],
  template: `
    <app-autocomplete
      [control]="searchInput"
      [suggestions]="suggestions"
      class="app-quick-search tw-min-w-0 tw-max-w-full tw-grow"
      placeholder="Claim number, Name, Employee ID, SSN or Policy number"
      [showHelpText]="false"
      [group]="false"
      #autocomplete
      (filter)="filter($event)"
      (select)="search($event)"
      [inputStyle]="dt"
    >
      <ng-template let-item="item">
        <span [innerHTML]="item.label"></span>
      </ng-template>
    </app-autocomplete>
  `,
  styles: ``,
})
export class QuickSearchComponent extends BaseComponent {
  dt: any = {
    background: 'var(--sed-warm-white-color)',
    border: '1px solid var(--sed-black-60-color)',
    fontSize: '14px',
    padding: '6px 10px',
    minHeight: '38px',
    boxSizing: 'border-box',
  };

  @ViewChild('autocomplete')
  autocomplete: AutocompleteComponent;

  searchInput = new FormControl<string>(null);

  protected suggestions: QuickSearchOption[];

  private add(value: string, type: QuickSearchType, label: string) {
    this.suggestions.push(new QuickSearchOption(value, type, label));
  }

  /** Trim and collapse runs of whitespace; keep spaces between words for full-name search. */
  private normalizeWhitespace(value: string): string {
    return value.trim().replace(/\s+/g, ' ');
  }

  filter(event: AutocompleteFilterEvent): void {
    this.suggestions = [];

    const normalized = this.normalizeWhitespace(event.value ?? '');
    if (!normalized) {
      return;
    }

    // Two or more name tokens → full name (value must keep a space for search-form split).
    // Tokens may include hyphens/apostrophes (e.g. Mary-Jane, O'Brien).
    if (multiWordNamePattern.test(normalized)) {
      this.add(
        normalized,
        'full-name',
        `Full name is <b>${normalized}</b>`,
      );
      const lastWord = normalized.split(/\s+/).pop()!;
      this.add(
        lastWord,
        'last-name',
        `Last name begins with <b>${lastWord}</b>`,
      );
    } else if (nameTokenPattern.test(normalized)) {
      this.add(
        normalized,
        'last-name',
        `Last name begins with <b>${normalized}</b>`,
      );
    } else {
      // Ignore spaces for IDs / SSN (e.g. "ABC 123" → "ABC123").
      const compact = normalized.replace(/\s/g, '');

      // Claim number / claim-event: original alphanumeric + hyphen + dot rules.
      if (alphaNumericHyphenDotPattern.test(compact)) {
        this.add(
          compact,
          'claim-number',
          `Claim number is <b>${compact}</b>`,
        );
        this.add(
          compact,
          'claim-event-number',
          `Claim/Event number begins with <b>${compact}</b>`,
        );
      }

      // Policy / employee ID: alphanumeric + hyphen + apostrophe.
      if (searchIdCompactPattern.test(compact)) {
        this.add(
          compact,
          'policy-number',
          `Policy number begins with <b>${compact}</b>`,
        );
        this.add(
          compact,
          'employee-id',
          `Employee ID begins with <b>${compact}</b>`,
        );
      }

      const digitGroups = compact.match(/\d+/g);
      const digits = digitGroups ? digitGroups.join('') : '';

      if (/^\d{9}$/.test(digits)) {
        this.add(
          compact,
          'ssn',
          `Social security number is <b>${compact}</b>`,
        );
      }

      if (/^\d{4}$/.test(digits)) {
        this.add(
          compact,
          'ssn-last-4',
          `Social security number (last 4) is <b>${compact}</b>`,
        );
      }
    }
  }

  search(value: any) {
    if (value instanceof QuickSearchOption) {
      this.router.navigate(['/view'], {
        queryParams: { tab: 'search', quicksearch: JSON.stringify(value) },
        queryParamsHandling: 'merge',
      });
      this.searchInput.setValue(null);
    }
  }
}

export class QuickSearchOption {
  constructor(
    public value: string,
    public type: QuickSearchType,
    public label: string,
  ) {}
}

export type QuickSearchType =
  | 'full-name'
  | 'last-name'
  | 'claim-number'
  | 'claim-event-number'
  | 'policy-number'
  | 'employee-id'
  | 'ssn'
  | 'ssn-last-4';
